import React from 'react';
import { Box, Typography, Radio } from '@mui/material';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { 
  REVIEW_TYPES,
  getReviewTypeColor,
  getReviewTypeBorderColor,
  getReviewTypeTextColor
} from '../../constants/reviewTypes';
import { COLORS } from '../../constants/radar';

const TypeSelection = ({ selectedType, onTypeChange, readOnly }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      {readOnly ? 'Review process type:' : 'Choose the type of review process:'}
    </Typography>
    <Box sx={{ 
      display: 'flex',
      justifyContent: 'center',
      gap: 4,
      mt: 8
    }}>
      <Box
        onClick={() => !readOnly && onTypeChange(REVIEW_TYPES.SEQUENTIAL)}
        sx={{
          cursor: readOnly ? 'default' : 'pointer',
          p: 4,
          borderRadius: 2,
          border: '2px solid',
          width: '380px',
          height: '380px',
          display: 'flex',
          flexDirection: 'column',
          borderColor: selectedType === REVIEW_TYPES.SEQUENTIAL 
            ? getReviewTypeBorderColor(REVIEW_TYPES.SEQUENTIAL) 
            : '#e2e8f0',
          backgroundColor: selectedType === REVIEW_TYPES.SEQUENTIAL 
            ? getReviewTypeColor(REVIEW_TYPES.SEQUENTIAL)
            : '#fff',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: readOnly 
              ? (selectedType === REVIEW_TYPES.SEQUENTIAL ? getReviewTypeBorderColor(REVIEW_TYPES.SEQUENTIAL) : '#e2e8f0')
              : getReviewTypeBorderColor(REVIEW_TYPES.SEQUENTIAL),
            backgroundColor: readOnly 
              ? (selectedType === REVIEW_TYPES.SEQUENTIAL ? getReviewTypeColor(REVIEW_TYPES.SEQUENTIAL) : '#fff')
              : '#F8FAFC'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 2
        }}>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            mb: 1
          }}>
            <Radio
              checked={selectedType === REVIEW_TYPES.SEQUENTIAL}
              onChange={() => !readOnly && onTypeChange(REVIEW_TYPES.SEQUENTIAL)}
              disabled={readOnly}
              sx={{ 
                color: '#94a3b8',
                '&.Mui-checked': {
                  color: getReviewTypeBorderColor(REVIEW_TYPES.SEQUENTIAL),
                }
              }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: selectedType === REVIEW_TYPES.SEQUENTIAL 
                  ? getReviewTypeTextColor(REVIEW_TYPES.SEQUENTIAL)
                  : COLORS.PRIMARY
              }}
            >
              Sequential
            </Typography>
          </Box>
          <LinearScaleIcon sx={{ 
            fontSize: 48,
            color: selectedType === REVIEW_TYPES.SEQUENTIAL 
              ? getReviewTypeTextColor(REVIEW_TYPES.SEQUENTIAL)
              : '#64748B' 
          }} />
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary"
          component="div"
          sx={{ pl: 0 }}
        >
          <ul>
            <li><b>Description:</b> Reviewers provide their feedback one after another, following a specific sequence.</li>
            <li><b>How it works:</b> Each reviewer can see and be influenced by the feedback given before them.</li>
            <li><b>Use this in case of evaluating:</b>
              <ul>
                <li>Technical skills related to technology</li>
              </ul>
            </li>
          </ul>
        </Typography>
      </Box>

      <Box
        onClick={() => !readOnly && onTypeChange(REVIEW_TYPES.PARALLEL)}
        sx={{
          cursor: readOnly ? 'default' : 'pointer',
          p: 4,
          borderRadius: 2,
          border: '2px solid',
          width: '380px',
          height: '380px',
          display: 'flex',
          flexDirection: 'column',
          borderColor: selectedType === REVIEW_TYPES.PARALLEL 
            ? getReviewTypeBorderColor(REVIEW_TYPES.PARALLEL)
            : '#e2e8f0',
          backgroundColor: selectedType === REVIEW_TYPES.PARALLEL 
            ? getReviewTypeColor(REVIEW_TYPES.PARALLEL)
            : '#fff',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: readOnly 
              ? (selectedType === REVIEW_TYPES.PARALLEL ? getReviewTypeBorderColor(REVIEW_TYPES.PARALLEL) : '#e2e8f0')
              : getReviewTypeBorderColor(REVIEW_TYPES.PARALLEL),
            backgroundColor: readOnly 
              ? (selectedType === REVIEW_TYPES.PARALLEL ? getReviewTypeColor(REVIEW_TYPES.PARALLEL) : '#fff')
              : '#F8FAFC'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 2
        }}>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            mb: 1
          }}>
            <Radio
              checked={selectedType === REVIEW_TYPES.PARALLEL}
              onChange={() => !readOnly && onTypeChange(REVIEW_TYPES.PARALLEL)}
              disabled={readOnly}
              sx={{ 
                color: '#94a3b8',
                '&.Mui-checked': {
                  color: getReviewTypeBorderColor(REVIEW_TYPES.PARALLEL),
                }
              }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                color: selectedType === REVIEW_TYPES.PARALLEL 
                  ? getReviewTypeTextColor(REVIEW_TYPES.PARALLEL)
                  : COLORS.PRIMARY
              }}
            >
             Parallel 
            </Typography>
          </Box>
          <ViewStreamIcon sx={{ 
            fontSize: 48,
            color: selectedType === REVIEW_TYPES.PARALLEL 
              ? getReviewTypeTextColor(REVIEW_TYPES.PARALLEL)
              : '#64748B' 
          }} />
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary"
          component="div"
          sx={{ pl: 0 }}
        >
          <ul>
            <li><b>Description:</b> Several reviewers provide feedback at the same time.</li>
            <li><b>How it works:</b> Each reviewer’s input is independent — no one waits for or is influenced by others’ comments.</li>
            <li><b>Use this in case of evaluating:</b>
              <ul>
                <li>Universal soft skills</li>
                <li>Representative soft skills</li>
                <li>Representative technical skills</li>
                <li>Role-based skills</li>
              </ul>
            </li>
          </ul>
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default TypeSelection; 